import React, { useEffect, useMemo, useState } from 'react';
import {
  FiClock, FiEye, FiEdit3, FiX, FiCalendar, FiChevronLeft, FiChevronRight,
  FiImage, FiLoader, FiAlertCircle, FiTrash2, FiSend,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

// =====================================================================
// QueueCalendarView — weekly grid of scheduled posts with previews.
//
// Pairs the pending queue rows (sorted by priority + created) with the
// projected upcoming_slots from analytics, then groups by day. Each day
// is a column; each post is a stacked card showing time + preview +
// category. Click a card to see full text in a modal.
//
// Renders inside ContentQueueManager as an alternative to the list view.
// =====================================================================

type QueueRow = {
  id: string;
  text: string;
  content_category: string;
  status: string;
  priority: number;
  scheduled_at: string | null;
  created_at: string;
  media_url?: string | null;
};

type Props = {
  pendingRows: QueueRow[];      // status IN ('pending', 'approved')
  upcomingSlots: string[];       // ISO strings from projected_schedule
  onEdit?: (row: QueueRow) => void;
  onRefresh?: () => void;
};

const CATEGORY_COLOR: Record<string, string> = {
  general:       'bg-white/10 text-gray-300 border-white/20',
  verdict:       'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  discipline:    'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  macro:         'bg-amber-500/20 text-amber-300 border-amber-500/40',
  lesson:        'bg-purple-500/20 text-purple-300 border-purple-500/40',
  question:      'bg-sky-500/20 text-sky-300 border-sky-500/40',
  hook:          'bg-rose-500/20 text-rose-300 border-rose-500/40',
  thread_teaser: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40',
  meme:          'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
};

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayLabel(d: Date, todayKey: string, tomorrowKey: string): { primary: string; secondary: string } {
  const k = dayKey(d);
  if (k === todayKey)    return { primary: 'Today',    secondary: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
  if (k === tomorrowKey) return { primary: 'Tomorrow', secondary: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
  return {
    primary:   d.toLocaleDateString(undefined, { weekday: 'short' }),
    secondary: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
}

const QueueCalendarView: React.FC<Props> = ({ pendingRows, upcomingSlots, onEdit, onRefresh }) => {
  const [weekOffset, setWeekOffset] = useState(0);           // 0 = current week starting today
  const [selectedPost, setSelectedPost] = useState<{ row: QueueRow; slotIso: string } | null>(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [postingNow, setPostingNow] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [postedInfo, setPostedInfo] = useState<{ url: string | null; with_media: boolean } | null>(null);

  // Reset per-tweet state whenever a different tweet is selected. Otherwise
  // the "already posted" banner + disabled Post-now button from tweet A
  // bleed into tweet B when the user clicks another card.
  useEffect(() => {
    setPostedInfo(null);
    setActionError(null);
    setImageError(null);
    setPostingNow(false);
    setDeleting(false);
    setGeneratingImage(false);
  }, [selectedPost?.row.id]);

  const postTweetNow = async () => {
    if (!selectedPost) return;
    if (!confirm('Post this tweet to Twitter NOW? This bypasses the schedule.')) return;
    setPostingNow(true);
    setActionError(null);
    setPostedInfo(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=post-tweet-now', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ queue_id: selectedPost.row.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      // Update local row status so the UI locks the Post-now button and
      // shows the "posted" banner persistently — even if parent refetches.
      setSelectedPost({
        ...selectedPost,
        row: { ...selectedPost.row, status: 'posted' },
      });
      setPostedInfo({ url: json?.posted?.url || null, with_media: !!json?.posted?.with_media });
      // Refresh parent list AFTER updating local state so the banner survives.
      onRefresh?.();
    } catch (e: any) {
      setActionError(e?.message || String(e));
    } finally {
      setPostingNow(false);
    }
  };

  const deleteTweet = async () => {
    if (!selectedPost) return;
    if (!confirm('Delete this tweet permanently? It will NOT be posted.')) return;
    setDeleting(true);
    setActionError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=delete-tweet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ queue_id: selectedPost.row.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setSelectedPost(null);
      onRefresh?.();
    } catch (e: any) {
      setActionError(e?.message || String(e));
    } finally {
      setDeleting(false);
    }
  };

  const generateImageForPost = async () => {
    if (!selectedPost) return;
    if (selectedPost.row.media_url) {
      if (!confirm('This tweet already has an image. Delete it and regenerate?')) return;
      await supabase.from('tweet_queue').update({ media_url: null }).eq('id', selectedPost.row.id);
    }
    setGeneratingImage(true);
    setImageError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ queue_id: selectedPost.row.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      // Update the selected row locally so preview shows immediately
      setSelectedPost({
        ...selectedPost,
        row: { ...selectedPost.row, media_url: json.image_url },
      });
      onRefresh?.();
    } catch (e: any) {
      setImageError(e?.message || String(e));
    } finally {
      setGeneratingImage(false);
    }
  };

  const deleteImageForPost = async () => {
    if (!selectedPost) return;
    if (!confirm('Delete image? Tweet will post without media.')) return;
    await supabase.from('tweet_queue').update({ media_url: null }).eq('id', selectedPost.row.id);
    setSelectedPost({ ...selectedPost, row: { ...selectedPost.row, media_url: null } });
    onRefresh?.();
  };

  // Pair each slot with a pending row by index (both sorted priority-first).
  // Pending rows should ALREADY be sorted by the parent (priority DESC,
  // scheduled_at NULLS LAST, created_at ASC).
  const pairedSlots = useMemo(() => {
    return upcomingSlots.map((slot, i) => ({
      slotIso: slot,
      row:     pendingRows[i] || null,
    })).filter(p => p.row);
  }, [pendingRows, upcomingSlots]);

  // Group pairs by day
  const grouped = useMemo(() => {
    const map = new Map<string, { date: Date; posts: typeof pairedSlots }>();
    for (const p of pairedSlots) {
      const d = new Date(p.slotIso);
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, { date: d, posts: [] });
      map.get(k)!.posts.push(p);
    }
    return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [pairedSlots]);

  // 7-day window from today (offset by weekOffset weeks)
  const now = new Date();
  const todayKey = dayKey(now);
  const tomorrowKey = dayKey(new Date(now.getTime() + 86400_000));

  const windowStart = new Date(now);
  windowStart.setDate(windowStart.getDate() + weekOffset * 7);
  windowStart.setHours(0, 0, 0, 0);

  const days: { date: Date; posts: typeof pairedSlots }[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(windowStart);
    d.setDate(d.getDate() + i);
    const k = dayKey(d);
    const found = grouped.find(g => dayKey(g.date) === k);
    days.push(found || { date: d, posts: [] });
  }

  const totalPostsInWindow = days.reduce((s, d) => s + d.posts.length, 0);

  return (
    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.03] p-5">
      {/* Header + navigation */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FiCalendar className="w-4 h-4 text-cyan-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
            Weekly calendar · {totalPostsInWindow} posts scheduled
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekOffset(o => o - 1)}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.06]"
            title="Previous week"
          >
            <FiChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest ${weekOffset === 0 ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40' : 'text-slate-400 hover:text-white'}`}
          >
            Today
          </button>
          <button
            onClick={() => setWeekOffset(o => o + 1)}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.06]"
            title="Next week"
          >
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 7-day grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {days.map(day => {
          const label = dayLabel(day.date, todayKey, tomorrowKey);
          const isToday = dayKey(day.date) === todayKey;
          return (
            <div
              key={dayKey(day.date)}
              className={`rounded-lg border p-2 min-h-[140px] ${isToday ? 'border-cyan-500/40 bg-cyan-500/[0.05]' : 'border-white/10 bg-white/[0.02]'}`}
            >
              <div className="flex items-baseline justify-between mb-2 pb-1 border-b border-white/10">
                <div>
                  <div className={`text-xs font-mono uppercase tracking-widest ${isToday ? 'text-cyan-300' : 'text-slate-400'}`}>
                    {label.primary}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500">{label.secondary}</div>
                </div>
                <div className="text-[10px] font-mono text-gray-500">
                  {day.posts.length}{day.posts.length ? ` post${day.posts.length === 1 ? '' : 's'}` : ''}
                </div>
              </div>

              {day.posts.length === 0 ? (
                <div className="text-[10px] text-gray-600 font-mono text-center py-4">
                  — quiet —
                </div>
              ) : (
                <div className="space-y-1.5">
                  {day.posts.map(p => {
                    const slotD = new Date(p.slotIso);
                    const timeStr = `${String(slotD.getHours()).padStart(2, '0')}:${String(slotD.getMinutes()).padStart(2, '0')}`;
                    return (
                      <button
                        key={p.slotIso}
                        onClick={() => setSelectedPost(p as any)}
                        className="w-full text-left rounded border border-white/10 bg-black/30 hover:border-cyan-500/40 hover:bg-cyan-500/5 p-1.5 transition-colors group"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-mono text-cyan-400 tabular-nums">{timeStr}</span>
                          <div className="flex items-center gap-1">
                            {p.row!.media_url && (
                              <FiImage className="w-2.5 h-2.5 text-fuchsia-400" title="Has image" />
                            )}
                            <span className={`text-[8px] font-mono uppercase tracking-widest px-1 rounded border ${CATEGORY_COLOR[p.row!.content_category] || CATEGORY_COLOR.general}`}>
                              {p.row!.content_category.slice(0, 4)}
                            </span>
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-300 line-clamp-3 leading-tight">
                          {p.row!.text.slice(0, 100)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-3 flex-wrap text-[10px] font-mono text-gray-500">
        <span>Click any post to preview full text</span>
      </div>

      {/* Preview modal */}
      {selectedPost && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedPost(null)}
        >
          <div
            className="max-w-2xl w-full rounded-2xl border border-cyan-500/40 bg-slate-900/95 backdrop-blur-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FiClock className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-white font-semibold">
                    Scheduled for {new Date(selectedPost.slotIso).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${CATEGORY_COLOR[selectedPost.row.content_category] || CATEGORY_COLOR.general}`}>
                    {selectedPost.row.content_category}
                  </span>
                  <span className="text-[10px] font-mono text-gray-500">
                    {selectedPost.row.text.length}/280 chars
                  </span>
                  {selectedPost.row.priority !== 0 && (
                    <span className="text-[10px] font-mono text-amber-400">
                      priority {selectedPost.row.priority}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.06]"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Tweet preview — mimic Twitter card style */}
            <div className="rounded-lg border border-white/20 bg-black/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold">
                  S
                </div>
                <div>
                  <div className="text-sm text-white font-semibold">SnapChart AI</div>
                  <div className="text-xs text-gray-500">@JeanMichelNews</div>
                </div>
              </div>
              <div className="text-white text-[15px] leading-relaxed whitespace-pre-wrap">
                {selectedPost.row.text}
              </div>
              {selectedPost.row.media_url && (
                <div className="mt-3 relative group">
                  <img
                    src={selectedPost.row.media_url}
                    alt="Tweet media"
                    className="rounded-lg border border-white/10 max-h-80 w-auto object-cover"
                  />
                  <button
                    onClick={deleteImageForPost}
                    className="absolute top-2 right-2 p-1.5 rounded bg-black/70 border border-rose-500/40 text-rose-300 opacity-0 group-hover:opacity-100 hover:bg-rose-500/20 transition-opacity"
                    title="Remove image"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            {imageError && (
              <div className="mt-3 text-xs font-mono text-rose-400 flex items-start gap-1">
                <FiAlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Image gen failed: {imageError}
              </div>
            )}
            {actionError && (
              <div className="mt-3 text-xs font-mono text-rose-400 flex items-start gap-1">
                <FiAlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {actionError}
              </div>
            )}
            {postedInfo && (
              <div className="mt-3 text-xs font-mono text-emerald-300 flex items-center gap-2 flex-wrap">
                <FiSend className="w-3 h-3" /> Posted{postedInfo.with_media ? ' with image' : ' (text-only)'}.
                {postedInfo.url && (
                  <a href={postedInfo.url} target="_blank" rel="noreferrer" className="underline hover:text-emerald-200">
                    View on X →
                  </a>
                )}
              </div>
            )}

            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={generateImageForPost}
                  disabled={generatingImage || postingNow || deleting}
                  className="px-3 py-1.5 rounded-md bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-100 text-xs font-mono hover:bg-fuchsia-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title="Fable 5 + FLUX 1.1 Pro (~$0.04, 8-12s)"
                >
                  {generatingImage
                    ? <FiLoader className="w-3 h-3 animate-spin" />
                    : <FiImage className="w-3 h-3" />}
                  {generatingImage
                    ? 'Generating…'
                    : selectedPost.row.media_url
                      ? 'Regenerate image'
                      : 'Generate image'}
                </button>
                <button
                  onClick={postTweetNow}
                  disabled={postingNow || generatingImage || deleting || selectedPost.row.status === 'posted'}
                  className="px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-xs font-mono hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title="Post NOW — bypasses schedule. Auto-generates image if missing."
                >
                  {postingNow
                    ? <FiLoader className="w-3 h-3 animate-spin" />
                    : <FiSend className="w-3 h-3" />}
                  {postingNow ? 'Posting…' : selectedPost.row.status === 'posted' ? 'Already posted' : 'Post now'}
                </button>
                <button
                  onClick={deleteTweet}
                  disabled={deleting || postingNow || generatingImage}
                  className="px-3 py-1.5 rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 text-xs font-mono hover:bg-rose-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title="Permanently delete this tweet"
                >
                  {deleting
                    ? <FiLoader className="w-3 h-3 animate-spin" />
                    : <FiTrash2 className="w-3 h-3" />}
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {onEdit && (
                  <button
                    onClick={() => { onEdit(selectedPost.row); setSelectedPost(null); }}
                    className="px-3 py-1.5 rounded-md bg-white/[0.06] border border-white/10 text-slate-200 text-xs font-mono hover:bg-white/[0.10] flex items-center gap-1.5"
                  >
                    <FiEdit3 className="w-3 h-3" /> Edit
                  </button>
                )}
                <button
                  onClick={() => setSelectedPost(null)}
                  className="px-3 py-1.5 rounded-md bg-cyan-500/20 border border-cyan-500/40 text-cyan-100 text-xs font-mono hover:bg-cyan-500/30"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueCalendarView;
